import React, { useCallback , useState } from "react";
import Table, { TableRow, TableCell } from "@amzn/meridian/table";
import Pagination from "@amzn/meridian/pagination";
import { FormattedMessage, injectIntl, intlShape } from "react-intl";
import Link from "@amzn/meridian/link";
import Input from "@amzn/meridian/input";
import Row from "@amzn/meridian/column";
import Column from "@amzn/meridian/column";
import Select, { SelectOption } from "@amzn/meridian/select";
import {
    compareTasksByPriority,
    compareTasksByExpiry,
    compareTasksByName,
    getPendingTasks,
    filterTasksByCategories,
    filterTasksByExpiry,
    filterTasksByAssignees,
    filterTasksByAssociateName,
    filterTasksByManagerName,
    filterTasksByName,
    filterTasksByProcessName,
    filterTasksByTaskType,
    filterTasksByStartedBy,
    filterTasksByPriority,
    priorityMapper
} from "../../../utils/Helper";
import { handleTableInputFocus } from "../../../utils/tableScrollUtils";
import useFilterStore from "../../../stores/FilterStore";
import useTaskStore from "../../../stores/TaskStore";
import TaskTableRow from "./TaskTableRow";
import {TASK_PRIORITY} from "../../../utils/Constants";

const TASK_TABLE_COLUMNS = [
  "priority",
  "taskName",
  "process",
  "taskType",
  "associate",
  "manager",
  "startedBy",
  "expiry",
  "",
];

const itemsPerPage = 20;

function TaskTable({ intl }) {
  /****************
   *     hooks    *
   ****************/

  const [managerFilter, setManagerFilter] = useState("");
  const [associateFilter, setAssociateFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState([]);
  const [taskNameFilter, setTaskNameFilter] = useState("");
  const [processFilter, setProcessFilter] = useState("");
  const [taskTypeFilter, setTaskTypeFilter] = useState("");
  const [startedByFilter, setStartedByFilter] = useState("");

  const tasks = useTaskStore((state) => state.tasks);

  const process = useFilterStore((state) => state.process);
  const expiry = useFilterStore((state) => state.expiry);
  const assignee = useFilterStore((state) => state.assignee);
  const setExpiry = useFilterStore((state) => state.setExpiry);

  const currentPage = useFilterStore((state) => state.page);
  const setCurrentPage = useFilterStore((state) => state.setPage);

  const sortColumn = useFilterStore((state) => state.sortColumn);
  const setSortColumn = useFilterStore((state) => state.setSortColumn);
  const sortDirection = useFilterStore((state) => state.sortDirection);
  const setSortDirection = useFilterStore((state) => state.setSortDirection);

  const filteredTasks = getFilteredAndSortedTasks(
    tasks,
    {
      process,
      expiry,
      assignee,
      associateFilter,
      managerFilter,
      taskNameFilter,
      processFilter,
      taskTypeFilter,
      startedByFilter,
      priorityFilter,
    },
    sortColumn,
    sortDirection,
    intl
  );
  const numberOfPages = Math.ceil(filteredTasks.length / itemsPerPage);

  const isExpandAll = useFilterStore((state) => state.isExpandAll);
  const setIsExpandAll = useFilterStore((state) => state.setIsExpandAll);

  function isSortColumn(column) {
    if (
      TASK_TABLE_COLUMNS[0] === column ||
      TASK_TABLE_COLUMNS[1] === column ||
      TASK_TABLE_COLUMNS[TASK_TABLE_COLUMNS.length - 2] === column
    ) {
      return true;
    }
    return false;
  }

  /*******************
   *      handler    *
   *******************/

  const onSort = useCallback(({ sortColumn, sortDirection }) => {
    setSortDirection(sortDirection);
    setSortColumn(sortColumn);
  }, []);

  /********************
   *      renderer     *
   *********************/

  const renderHeaderRow = () => {
    return TASK_TABLE_COLUMNS.map((column) => {
      if (column === TASK_TABLE_COLUMNS[TASK_TABLE_COLUMNS.length - 1])
        return (
          <TableCell>
            <Link onClick={() => setIsExpandAll(!isExpandAll)}>
              {isExpandAll ? (
                <FormattedMessage
                  id="overview.taskTable.header.collapseAll"
                  defaultMessage="Collapse All"
                />
              ) : (
                <FormattedMessage
                  id="overview.taskTable.header.expandAll"
                  defaultMessage="Expand All"
                />
              )}
            </Link>
          </TableCell>
        );
      return (
        <TableCell
          key={column}
          sortColumn={isSortColumn(column) ? column : null}
        >
          <FormattedMessage id={`overview.taskTable.header.${column}`} />
        </TableCell>
      );
    });
  };

  const renderFilterRow = () => {
    return (
      <TableRow>
        <TableCell>
          <Row alignmentHorizontal="left" spacing="none">
            <Select value={priorityFilter} onChange={setPriorityFilter} placeholder="Priority" width={125} onFocus={handleTableInputFocus}>
              <SelectOption value={TASK_PRIORITY.CRITICAL} label={priorityMapper(TASK_PRIORITY.CRITICAL, intl).label} />
              <SelectOption value={TASK_PRIORITY.AT_RISK} label={priorityMapper(TASK_PRIORITY.AT_RISK, intl).label} />
              <SelectOption value={TASK_PRIORITY.LATE} label={priorityMapper(TASK_PRIORITY.LATE, intl).label} />
              <SelectOption value={TASK_PRIORITY.HIGH} label={priorityMapper(TASK_PRIORITY.HIGH, intl).label} />
              <SelectOption value={TASK_PRIORITY.MEDIUM} label={priorityMapper(TASK_PRIORITY.MEDIUM, intl).label} />
              <SelectOption value={TASK_PRIORITY.LOW} label={priorityMapper(TASK_PRIORITY.LOW, intl).label} />
            </Select>
          </Row>
        </TableCell>
        <TableCell>
          <Row alignmentHorizontal="left" spacing="none">
            <Input
              type="text"
              width="125px"
              size="small"
              value={taskNameFilter}
              onChange={setTaskNameFilter}
              onFocus={handleTableInputFocus}
            />
          </Row>
        </TableCell>
        <TableCell>
          <Row alignmentHorizontal="left" spacing="none">
            <Input
              type="text"
              width="100px"
              size="small"
              value={processFilter}
              onChange={setProcessFilter}
              onFocus={handleTableInputFocus}
            />
          </Row>
        </TableCell>
        <TableCell>
          <Row alignmentHorizontal="left" spacing="none">
            <Input
              type="text"
              width="100px"
              size="small"
              value={taskTypeFilter}
              onChange={setTaskTypeFilter}
              onFocus={handleTableInputFocus}
            />
          </Row>
        </TableCell>
        <TableCell>
          <Row alignmentHorizontal="left" spacing="none">
            <Input
              type="text"
              width="100px"
              size="small"
              value={associateFilter}
              onChange={setAssociateFilter}
              onFocus={handleTableInputFocus}
            />
          </Row>
        </TableCell>
        <TableCell>
          <Row alignmentHorizontal="left" spacing="none">
            <Input
              type="text"
              width="100px"
              size="small"
              value={managerFilter}
              onChange={setManagerFilter}
              onFocus={handleTableInputFocus}
            />
          </Row>
        </TableCell>
        <TableCell>
          <Row alignmentHorizontal="left" spacing="none">
            <Input
              type="text"
              width="100px"
              size="small"
              value={startedByFilter}
              onChange={setStartedByFilter}
              onFocus={handleTableInputFocus}
            />
          </Row>
        </TableCell>
        <TableCell>
          <Row alignmentHorizontal="left" spacing="none">
            <Select
              value={expiry}
              onChange={setExpiry}
              placeholder="Expiry"
              width={150}
              onFocus={handleTableInputFocus}
            >
              <SelectOption value={Infinity} label="-" />
              <SelectOption value={1} label="<1hr" />
              <SelectOption value={4} label="<4hr" />
              <SelectOption value={8} label="<8hr" />
              <SelectOption value={24} label="<24hr" />
              <SelectOption value={24 * 7} label="<7days" />
            </Select>
          </Row>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <Row width="100%" widths="fill" className="enable-scroll-x">
      <Table
        headerRows={1}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onSort={onSort}
        showDividers={true}
        spacing="small"
        headerColumns={1}
        stickyHeaderColumn={true}
        stickyHeaderRow={true}
        fixHeaderRows={true}
        rowComponents={[TableRow, TaskTableRow]}
        nestedRows={true}
      >
        <TableRow>{renderHeaderRow()}</TableRow>
        {renderFilterRow()}
        {getTasksByPageNum(filteredTasks, currentPage).map((task) => (
          <TaskTableRow key={task.id} task={task} isExpandAll={isExpandAll} />
        ))}
      </Table>
      <Column alignmentHorizontal="center">
        <Pagination
          showSkipArrows={true}
          numberOfPages={numberOfPages}
          onChange={setCurrentPage}
          currentPage={currentPage}
        />
      </Column>
    </Row>
  );
}

TaskTable.propTypes = {
  intl: intlShape.isRequired,
};

const getFilteredAndSortedTasks = (
  tasks,
  {
    process,
    expiry,
    assignee,
    managerFilter,
    associateFilter,
    taskNameFilter,
    processFilter,
    taskTypeFilter,
    startedByFilter,
    priorityFilter,
  },
  sortColumn,
  sortDirection,
  intl
) => {
  let filteredTasks = getPendingTasks(tasks);
  if (process.length > 0)
    filteredTasks = filterTasksByCategories(filteredTasks, process);

  if (associateFilter.length > 0)
    filteredTasks = filterTasksByAssociateName(filteredTasks, associateFilter);

  if (managerFilter.length > 0)
    filteredTasks = filterTasksByManagerName(filteredTasks, managerFilter);

  if (taskNameFilter.length > 0)
    filteredTasks = filterTasksByName(filteredTasks, taskNameFilter);

  if (processFilter.length > 0)
    filteredTasks = filterTasksByProcessName(
      filteredTasks,
      processFilter,
      intl
    );

  if (taskTypeFilter.length > 0)
    filteredTasks = filterTasksByTaskType(filteredTasks, taskTypeFilter, intl);

  if (priorityFilter.length > 0)
    filteredTasks = filterTasksByPriority(filteredTasks, priorityFilter, intl);

  if (startedByFilter.length > 0)
    filteredTasks = filterTasksByStartedBy(filteredTasks, startedByFilter);

  if (expiry !== 0) filteredTasks = filterTasksByExpiry(filteredTasks, expiry);

  if (assignee.length > 0)
    filteredTasks = filterTasksByAssignees(filteredTasks, assignee);

  return filteredTasks.sort((a, b) => {
    // Handle Task Name sorting
    if (sortColumn === TASK_TABLE_COLUMNS[1]) {
      if (sortDirection === "ascending") {
        return compareTasksByName(a, b, true);
      }
      return compareTasksByName(a, b, false);
    }
    // Handle Expiry sorting
    if (sortColumn === TASK_TABLE_COLUMNS[TASK_TABLE_COLUMNS.length - 2]) {
      if (sortDirection === "ascending") {
        return (
          compareTasksByExpiry(a, b, true) || compareTasksByExpiry(a, b, true)
        );
      }
      return (
        compareTasksByExpiry(a, b, false) || compareTasksByExpiry(a, b, true)
      );
    }
    // Default Priority sorting
    if (sortDirection === "ascending") {
      return (
        compareTasksByPriority(a, b, true) || compareTasksByExpiry(a, b, true)
      );
    }
    return (
      compareTasksByPriority(a, b, false) || compareTasksByExpiry(a, b, true)
    );
  });
};

const getTasksByPageNum = (tasks, pageNum) => {
  const firstVisibleIndex = (pageNum - 1) * itemsPerPage;
  const lastVisibleIndex = firstVisibleIndex + itemsPerPage;
  return tasks.slice(firstVisibleIndex, lastVisibleIndex);
};

export default injectIntl(TaskTable);



// WEBPACK FOOTER //
// ./src/components/overview/TaskTable/TaskTable.jsx